<template>
    <div class="uv-wang-editor" style="border: 1px solid #ccc" v-loading="editorLoading">
      <Toolbar
        style="border-bottom: 1px solid #ccc"
        :editor="editorRef"
        :defaultConfig="toolbarConfig"
        :mode="mode"
      />
      <Editor
        :style="`height: ${height}px; overflow-y: hidden;`"
        v-model="valueHtml"
        :defaultConfig="editorConfig"
        :mode="mode"
        @onCreated="handleCreated"
		@customPaste="handleCustomPaste"
		@onChange="handleChange"
      />
    </div>
</template>

<script setup>
	import '@wangeditor/editor/dist/css/style.css' // 引入 css

	import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
	import { post } from '@/utils/request.js'
	import { useMessage } from '@/utils/message.js'
	import { fetchRemoteFile, uploadToAliyun } from '@/utils/file.js'

	const props = defineProps({ 
		uploadUrl: { type: String, required: true },
		placeholder: { type: String },
		modelValue: { type: String },
		height: { type: Number, default: 350 },
		mode: { type: String, default: 'default' }
	})
	
	const emits = defineEmits(['update:modelValue'])
	
	const { modelValue, placeholder, uploadUrl } = toRefs(props)
	
	// 编辑器实例，必须用 shallowRef
	const editorRef = shallowRef()
	const editorLoading = ref(false)
	const toolbarConfig = ref({})
	const editorConfig = ref({ 
		placeholder: placeholder.value,
		MENU_CONF: {
			uploadImage: {
				// 最多可上传几个文件，默认为 100
				maxNumberOfFiles: 5,
				// 小于该值就插入 base64 格式（而不上传），默认为 0
				base64LimitSize: 2 * 1024, // 2kb 
				// 自定义上传
				async customUpload(file, insertFn) {
					// file 即选中的文件
					const formData = new FormData()
					
					formData.append('file', file);
					//formData.append('fileName', file.name)
					formData.append('type', 'image');
					editorLoading.value = true
					// 自己实现上传，并得到图片 url alt href
					post(uploadUrl.value, formData, {
						headers: {
							'Content-Type': 'multipart/form-data', // 必须设置
						}
					}).then(res => {
						editorLoading.value = false
						if(res.code != 0) {
							return 
						}
						useMessage('上传图片成功！').notify('success')
						const { url, alt, href } = res.data
						// 最后插入图片
						insertFn(url, alt, href)
					})
				}, 
			},
			uploadVideo: {
				// 最多可上传几个文件，默认为 100
				maxNumberOfFiles: 2, 
				// 单个文件的最大体积限制，默认为 10M
				maxFileSize: 50 * 1024 * 1024, // 50M
				// 自定义上传
				async customUpload(file, insertFn) {
					editorLoading.value = true
					
					const url = await uploadToAliyun(file,'videos');
					
					url && insertFn(url, url)
					
					editorLoading.value = false
					/*
					// file 即选中的文件
					const formData = new FormData()
					
					formData.append('file', file);
					//formData.append('fileName', file.name)
					formData.append('type', 'video');
					editorLoading.value = true
					// 自己实现上传，并得到视频 url poster
					post(uploadUrl.value, formData, {
						headers: {
							'Content-Type': 'multipart/form-data', // 必须设置
						}
					}).then(res => {
						editorLoading.value = false
						if(res.code != 0) {
							return 
						}
						useMessage('上传视频成功！').notify('success')
						const { url, poster } = res.data
						// 最后插入图片
						insertFn(url, poster)
					})
					*/
				}, 
			}
		}
	})
	
	// 内容 HTML
	const valueHtml = ref(modelValue.value)
	
	// 组件销毁时，也及时销毁编辑器
	onUnmounted(()=>{
		const editor = editorRef.value
		if (editor == null) return
		editor.destroy()
	})
	
	const handleCreated = (editor) => {
		editorRef.value = editor // 记录 editor 实例，重要！ 
		editor.focus(true) // 选区定位到最后
	}
	
	const handleChange = (editor) => {
		emits('update:modelValue', editor.getHtml())
	}
	
	const handlePasteHtml = async (html) => {
		const div = document.createElement('div')
		div.innerHTML = html
		const imgs = div.querySelectorAll('img')
		for (const img of imgs) {
			const src = img.getAttribute('src')
			if (!src) continue;

			// 只处理远程图片图片
			if (!src.startsWith('http')) continue;
			// 如果是自己的图
			if(src.startsWith('http://zggjjyzx')) continue;
			 
			const file = await fetchRemoteFile(src)
			if(file) {
				//const res = await post(uploadUrl.value, { url: src })
				const url = await uploadToAliyun(file,'images');
				
				img.setAttribute('src', url)
			}
		}
		
		return div.innerHTML
	}
	
	const handleCustomPaste = async (editor, event) => {
		// 阻止默认的粘贴行为
		event.preventDefault()
		editorLoading.value = true 
		
		const html = event.clipboardData.getData('text/html') // 获取粘贴的 html
		// const text = event.clipboardData.getData('text/plain') // 获取粘贴的纯文本
		// const rtf = event.clipboardData.getData('text/rtf') // 获取 rtf 数据（如从 word wsp 复制粘贴）

		const parsedHtml = await handlePasteHtml(html)
		
		// 异步
		setTimeout(() => {
			editor.dangerouslyInsertHtml(parsedHtml)
		}, 1000)
		editorLoading.value = false 
		return false
	}
	
	watch(modelValue, v => {
		valueHtml.value = v
	})
	
	defineExpose({
		getImages: () => {
			return editorRef.value.getElemsByType('image') // 所有图片
		}
	})
</script>

<style lang="scss" scoped>
	.uv-wang-editor {
		:deep(.w-e-text-placeholder) {
			top: 10px;
		}
	}
</style>